import type { ErrorEvent, EventHint } from '@sentry/react-router'

function sanitize(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return value.replace(/0x[0-9a-fA-F]{40,}/g, '[Filtered]').slice(0, 200)
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return value
  if (depth >= 3) return '[Filtered]'
  if (Array.isArray(value)) return value.slice(0, 50).map((item) => sanitize(item, depth + 1))
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 50)
        .map(([key, item]) => [
          key.replace(/0x[0-9a-fA-F]{40,}/g, '[Filtered]').slice(0, 200),
          /private.?key|secret|password|mnemonic|seed|token|authorization/i.test(key)
            ? '[Filtered]'
            : sanitize(item, depth + 1),
        ])
    )
  }
  return undefined
}

export function addRejectionContext(event: ErrorEvent, hint: EventHint): ErrorEvent {
  const rejection = hint.originalException
  const isRejection = event.exception?.values?.some(
    ({ mechanism }) => mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
  )
  if (!isRejection || typeof rejection !== 'object' || rejection === null) return event
  const prototype = Object.getPrototypeOf(rejection)
  if (prototype !== Object.prototype && prototype !== null) return event

  const { code, message, data } = rejection as Record<string, unknown>
  const context = { code: sanitize(code), message: sanitize(message), data: sanitize(data) }
  if (typeof code === 'number' || typeof code === 'string') {
    event.tags = { ...event.tags, 'rejection.code': String(sanitize(code)) }
  }
  event.contexts = { ...event.contexts, rejection: context }
  event.extra = { ...event.extra, __serialized__: context }
  return event
}
